import { createContext, type ComponentChildren } from "preact"
import { useCallback, useContext, useRef, useState } from "preact/hooks"

interface ConfirmContextValue {
	confirm: (message: string) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue>({
	confirm: () => Promise.resolve(false),
})

export function useConfirm(): (message: string) => Promise<boolean> {
	return useContext(ConfirmContext).confirm
}

// window.confirm/alert/prompt are unreliable on iOS/macOS Safari: they're a
// silent no-op once the app is installed to the home screen (standalone
// display mode), and even in a regular tab, Safari can auto-suppress repeat
// dialogs ("Don't allow this page to create additional dialogs") so later
// calls just return false with no visible sign. This modal is the
// replacement; keep using useConfirm() instead of window.confirm.
export function ConfirmProvider({ children }: { children: ComponentChildren }) {
	const [message, setMessage] = useState<string | null>(null)
	const resolveRef = useRef<((ok: boolean) => void) | null>(null)

	const confirm = useCallback((msg: string) => {
		return new Promise<boolean>((resolve) => {
			resolveRef.current = resolve
			setMessage(msg)
		})
	}, [])

	const settle = (ok: boolean) => {
		setMessage(null)
		resolveRef.current?.(ok)
		resolveRef.current = null
	}

	return (
		<ConfirmContext.Provider value={{ confirm }}>
			{children}
			{message !== null && (
				<>
					<div class="modal fade show d-block" role="dialog" aria-modal="true">
						<div class="modal-dialog modal-dialog-centered">
							<div class="modal-content">
								<div class="modal-body">{message}</div>
								<div class="modal-footer">
									<button
										type="button"
										class="btn btn-outline-secondary"
										onClick={() => settle(false)}
									>
										Cancel
									</button>
									<button
										type="button"
										class="btn btn-danger"
										onClick={() => settle(true)}
									>
										Delete
									</button>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-backdrop fade show" onClick={() => settle(false)} />
				</>
			)}
		</ConfirmContext.Provider>
	)
}
